/**
 * The roster — a grid of bibs. Search by name, bib or phone; filter by
 * status. Scrolling this list is how the committee reads the club.
 */

import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ageFrom, duesState, formatDate, TIERS } from '../../data';
import { useClubData, useGrounds } from '../../context/ClubContext';
import { useUI } from '../../context/UIContext';
import { BibCard } from '../../components/Bib';
import { MemberSheet } from '../../components/sheets/MemberSheet';
import { MemberCreatedSheet } from '../../components/sheets/MemberCreatedSheet';
import { Button, Empty, INPUT_CLS, Pill, Table, ViewTitle } from '../../components/ui';

// Filter is one of: 'all' | 'active' | 'lapsed' | 'junior' | 'left'
const FILTERS = ['all', 'active', 'lapsed', 'junior', 'left'];

/*
 * Grid or list. The bib grid is the club's face; the LIST is how you work a
 * roster once it stops fitting on a screen — the club plans for 500+ members
 * (owner's direction, 17 Aug 2026), and 250 tall cards is scrolling, not
 * scanning. The choice sticks per browser: it is a way of working, not a
 * per-visit whim.
 */
const VIEW_KEY = 'trc_members_view';

export function MembersPage() {
  const { db } = useClubData();
  const { openSheet } = useUI();
  const navigate = useNavigate();

  // The dashboard's counters deep-link here (?filter=lapsed and friends).
  const [params] = useSearchParams();
  const urlFilter = params.get('filter');

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState(FILTERS.includes(urlFilter) ? urlFilter : 'all');
  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'grid';
    } catch {
      return 'grid';
    }
  });

  function switchView(next) {
    setView(next);
    try {
      localStorage.setItem(VIEW_KEY, next);
    } catch {
      /* private mode — the choice just does not stick */
    }
  }

  /*
   * Two different meanings of "not active" live on this screen, and conflating
   * them is how a removed member goes on looking like a paying one:
   *   duesState()  — have they paid? (lapsed)
   *   status       — are they still in the club at all? (left)
   * Everything except the explicit "Left" filter is the current roster only.
   * `pending` members are NOT here at all — they live on Membership requests
   * until the committee activates them.
   */
  const roster = useMemo(
    () => db.members.filter((m) => m.status !== 'left' && m.status !== 'pending'),
    [db.members],
  );
  const left = useMemo(() => db.members.filter((m) => m.status === 'left'), [db.members]);
  const pendingCount = useMemo(
    () => db.members.filter((m) => m.status === 'pending').length,
    [db.members],
  );

  const counts = useMemo(
    () => ({
      all: roster.length,
      active: roster.filter((m) => duesState(m).key !== 'lapsed').length,
      lapsed: roster.filter((m) => duesState(m).key === 'lapsed').length,
      left: left.length,
    }),
    [roster, left],
  );

  const list = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = [...(filter === 'left' ? left : roster)].sort((a, b) => a.bib - b.bib);

    if (filter === 'active') rows = rows.filter((m) => duesState(m).key !== 'lapsed');
    if (filter === 'lapsed') rows = rows.filter((m) => duesState(m).key === 'lapsed');
    if (filter === 'junior') {
      rows = rows.filter((m) => m.tier === 'junior' || (ageFrom(m.dob) ?? 99) < 18);
    }

    if (q) {
      rows = rows.filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          String(m.bib).includes(q) ||
          (m.phone ?? '').includes(q),
      );
    }
    return rows;
  }, [roster, left, filter, search]);

  // Bibs are never reused, so the next one counts past members who have left.
  const nextBib = db.members.reduce((max, m) => Math.max(max, m.bib), 0) + 1;

  const filters = [
    ['all', `All ${counts.all}`],
    ['active', `Active ${counts.active}`],
    ['lapsed', `Lapsed ${counts.lapsed}`],
    ['junior', 'Juniors'],
    // Only offered once somebody has actually left.
    ...(counts.left ? [['left', `Left ${counts.left}`]] : []),
  ];

  return (
    <>
      <ViewTitle
        title="Members"
        sub={
          pendingCount
            ? `${counts.all} on the roster · ${pendingCount} awaiting activation under Requests`
            : `${counts.all} on the roster`
        }
      />

      <div className="mb-3.5 flex flex-wrap gap-2">
        {/*
          type="search" + autoComplete="off" + a name that looks like nothing
          Chrome recognises. Without them the browser saw a bare text input,
          decided it was a phone field, and AUTOFILLED it — which fires the
          React onChange, so the roster silently filtered itself to "No match"
          while the admin was typing a number into the add-member form. The
          give-away was the value never matching what was typed: "+918197091531"
          in the form, "08197091531" in the search box, both wearing Chrome's
          pale-blue autofill highlight.
        */}
        <input
          type="search"
          name="roster-filter"
          autoComplete="off"
          placeholder="Search name, bib or phone"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={`${INPUT_CLS} min-w-0 flex-1 max-sm:basis-full sm:min-w-[190px]`}
        />
        {/* On a phone the search box takes the whole row and this button
            drops beneath it, full width — side by side, the box was too
            narrow to show even its own placeholder. */}
        <Button
          tone="p"
          className="max-sm:w-full"
          onClick={() => {
            /*
             * Clear the search BEFORE the form opens, not only after a save.
             * Checking whether someone is already on the roster is the usual
             * reason to type here, and leaving that query live meant the list
             * behind the modal sat on "No match" for a number that was about
             * to become a member. The form does its own duplicate check now,
             * so the search box is not the tool for that job any more.
             */
            setSearch('');
            setFilter('all');
            openSheet(
              <MemberSheet
                nextBib={nextBib}
                onCreated={(id) => {
                  /*
                   * Clear the search and the status filter before showing the
                   * new member. Both survive the sheet, and a leftover query —
                   * typically the phone number just used to check the person
                   * was not already on the roster — hides the very row that was
                   * added, so the add looks like it silently failed.
                   */
                  setSearch('');
                  setFilter('all');
                  openSheet(<MemberCreatedSheet memberId={id} />);
                }}
              />,
            );
          }}
        >
          + Add member
        </Button>
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-2.5">
        {/* Segmented status filter — the active segment lifts to white and
            bolds. `text-stable` + `data-label` reserve the bold width so the
            row does not shove sideways when you change filter (see the
            utility in tailwind.config.js). */}
        <div className="inline-flex flex-wrap gap-1 border border-line bg-ground p-1">
          {filters.map(([key, label]) => {
            const on = filter === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setFilter(key)}
                data-label={label}
                className={`text-stable whitespace-nowrap px-3 py-1.5 font-market text-[10.5px] uppercase tracking-wide transition-colors duration-200 ${
                  on
                    ? 'bg-white font-bold text-ink-700 shadow-sm'
                    : 'text-copy-muted hover:text-ink-700'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Grid ↔ list. The grid is the club's face; the list is how a big
            roster gets worked. */}
        <div className="inline-flex gap-1 border border-line bg-ground p-1">
          {[
            ['grid', 'Grid', <GridIcon key="grid" />],
            ['list', 'List', <ListIcon key="list" />],
          ].map(([key, label, icon]) => {
            const on = view === key;
            return (
              <button
                key={key}
                type="button"
                aria-pressed={on}
                onClick={() => switchView(key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 font-market text-[10.5px] uppercase tracking-wide transition-colors duration-200 ${
                  on
                    ? 'bg-white font-bold text-ink-700 shadow-sm'
                    : 'text-copy-muted hover:text-ink-700'
                }`}
              >
                {icon}
                <span data-label={label} className="text-stable">
                  {label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {list.length ? (
        view === 'grid' ? (
          <div className="grid grid-cols-1 gap-3 min-[720px]:grid-cols-2">
            {list.map((m) => (
              <BibCard
                key={m.id}
                member={m}
                results={db.results}
                onOpen={(id) => {
                  navigate(`/a/members/${id}`);
                  window.scrollTo(0, 0);
                }}
              />
            ))}
          </div>
        ) : (
          <MemberListTable
            members={list}
            onOpen={(id) => {
              navigate(`/a/members/${id}`);
              window.scrollTo(0, 0);
            }}
          />
        )
      ) : search ? (
        <Empty title="No match">Nothing for “{search}”.</Empty>
      ) : (
        <Empty title="No one here">Add your first runner.</Empty>
      )}
    </>
  );
}

/**
 * The roster as rows — one line per member, the same facts the bib card
 * carries (bib, name, tier, ground, dues) plus the phone the search box
 * already matches on. Dense on purpose: at hundreds of members this is the
 * form that still scans.
 */
function MemberListTable({ members, onOpen }) {
  const grounds = useGrounds();
  return (
    <Table
      head={
        <tr>
          <th>Bib</th>
          <th>Member</th>
          <th>Age</th>
          <th>Phone</th>
          <th>Tier</th>
          <th>Ground</th>
          <th>Paid till</th>
          <th>Status</th>
        </tr>
      }
    >
      {members.map((m) => {
        const dues = duesState(m);
        const age = ageFrom(m.dob);
        return (
          <tr
            key={m.id}
            className="cursor-pointer hover:bg-ground-warm"
            onClick={() => onOpen(m.id)}
          >
            <td className="font-semibold tabular-nums">
              {String(m.bib).padStart(3, '0')}
            </td>
            <td className="whitespace-nowrap font-market font-bold text-ink-700">{m.name}</td>
            <td className="tabular-nums">{age ?? '—'}</td>
            <td className="whitespace-nowrap tabular-nums">{m.phone ?? '—'}</td>
            <td>{TIERS[m.tier].label}</td>
            <td className="whitespace-nowrap">{grounds[m.ground]?.split(' ')[0] ?? '—'}</td>
            <td className="whitespace-nowrap tabular-nums">
              {m.tier === 'life' ? '—' : m.duesPaidTill ? formatDate(m.duesPaidTill) : 'Never'}
            </td>
            <td>
              <Pill tone={dues.cls}>{dues.label}</Pill>
            </td>
          </tr>
        );
      })}
    </Table>
  );
}

function GridIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
      <path d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );
}
